import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IoIosMenu } from 'react-icons/io';
import useCurrentUser, { useOrganization } from '../../hooks/useCurrentUser';
import styles from './TopBarContainer.module.css';

import {
  AsyncImgLoader,
  HelpMenu,
  NewFeatureModal,
  NewFeaturePopup,
  TopBar,
  TopBarMenu,
  TopBarMenuItem,
  HighlightedNumber,
  DownloadIcon,
} from '@energybox/react-ui-library/dist/components';

import CompanyLogo from '@energybox/react-ui-library/dist/icons/CompanyLogo';
import {
  User as UserIcon,
  Organization as OrganizationIcon,
} from '@energybox/react-ui-library/dist/icons';
import { menuWithMobileStyles } from '@energybox/react-ui-library/dist/components/TopBar';
import { logout, getCurrentUser, toggleMobileMenu } from '../../actions/app';
import { getCurrentOrganization } from '../../actions/organizations';
import { ApplicationState } from '../../reducers';
import history from '../../history';
import { global, classNames } from '@energybox/react-ui-library/dist/utils';
import { useLocation } from 'react-router-dom';
import { useDownloadStatus } from '../../hooks/reportworker/useDownloadStatus';
import {
  dismissNewFeature,
  getAttachmentUrl,
  getNewFeature,
  getNewFeatureVisited,
  viewNewFeature,
} from '../../actions/whatsNew';
import {
  FeaturesByMonth,
  FeatureStatus,
} from '@energybox/react-ui-library/dist/types/Feature';
import {
  ImgDom,
  Renderer,
} from '@energybox/react-ui-library/dist/types/ParsedHTML';
import {
  isAtlassianImg,
  render,
} from '@energybox/react-ui-library/dist/utils/parsedHTML';

const TopBarContainer = ({ noNavigation }: { noNavigation?: boolean }) => {
  const dispatch = useDispatch();

  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const isSimplyCheckView = queryParams.get('app') === 'simplycheck';

  const currentOrganization = useOrganization();
  const user = useCurrentUser();
  const isMobileMenuOpen = useSelector(
    ({ app }: ApplicationState) => app.isMobileMenuOpen
  );

  const saveFileId = useSelector(({ reportworker }: ApplicationState) =>
    reportworker.saveFileIds.filter((item) => item)
  );

  useDownloadStatus();

  useEffect(() => {
    if (!user) dispatch(getCurrentUser());
    if (!currentOrganization) dispatch(getCurrentOrganization());
    dispatch(getNewFeature());
  }, []);

  const [popupShowing, showPopup] = useState(false);
  const [modalShowing, setModalShowing] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user) dispatch(getNewFeatureVisited(user.id));
  }, [user]);

  const {
    newFeatures: features,
    featureStatus: viewStatus,
  }: {
    newFeatures?: FeaturesByMonth;
    featureStatus: FeatureStatus;
  } = useSelector(
    ({ whatsNew: { newFeatures, featureStatus } }: ApplicationState) => ({
      newFeatures,
      featureStatus,
    })
  );

  const cancelDelayedPopup = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (viewStatus === FeatureStatus.HAS_UPDATE) {
      if (user)
        timerRef.current = setTimeout(() => {
          showPopup(true);
          timerRef.current = null;
        }, 2000);
      // Delay the pop-up for 2 seconds for better UX
    } else {
      showPopup(false);
      cancelDelayedPopup();
    }
    return cancelDelayedPopup;
  }, [viewStatus]);

  const showModal = () => {
    if (user) {
      dispatch(viewNewFeature(user.id));
      setModalShowing(true);
    }
  };

  const dismissPopup = () => {
    dispatch(dismissNewFeature());
  };

  const img: Renderer<ImgDom> = (dom) => {
    if (isAtlassianImg(dom)) {
      return (
        <AsyncImgLoader
          src={async () => getAttachmentUrl(dom)}
          width="100%"
          originalSize={{
            width: parseInt(dom.attributes['data-width']),
            height: parseInt(dom.attributes['data-height']),
          }}
        />
      );
    }
    return <img {...dom.attributes} />;
  };

  let notifyStatus = useSelector(
    ({ reportworker }: ApplicationState) => reportworker.notifyStatus
  );

  let showHideIcon = useSelector(
    ({ reportworker }: ApplicationState) => reportworker.toggleIcon
  );

  if (noNavigation) return <div />;

  return (
    <TopBar
      showPopup={popupShowing}
      popup={<NewFeaturePopup onAction={showModal} onDismiss={dismissPopup} />}
    >
      <div>
        <button
          className={classNames(
            menuWithMobileStyles.mobileMenuButton,
            isMobileMenuOpen ? menuWithMobileStyles.glowingButton : null
          )}
          onClick={() => dispatch(toggleMobileMenu())}
        >
          <IoIosMenu
            className={menuWithMobileStyles.mobileMenuIcon}
            size="24"
          />
        </button>
        <CompanyLogo
          className={menuWithMobileStyles.companyLogoWithoutText}
          withText={false}
          width="18"
          height="18"
        />
      </div>
      <div>
        {features && (
          <>
            <HelpMenu whatsNewOnClick={showModal} />

            <NewFeatureModal
              features={features}
              isModalShowing={modalShowing}
              onClose={() => setModalShowing(false)}
              render={render({ img })}
            />
          </>
        )}

        {user && (
          <div
            className={classNames(
              menuWithMobileStyles.menuContainer,
              styles.organizationMenuContainer
            )}
          >
            <OrganizationIcon color="var(--accent-base)" size={14} />
            <span className={styles.organizationMenuTitle}>
              {currentOrganization
                ? currentOrganization.title
                : global.NOT_AVAILABLE}
            </span>
          </div>
        )}

        {user && (
          <div className={menuWithMobileStyles.mobileMenuContainer}>
            <TopBarMenu
              key={'org-menu'}
              icon={<OrganizationIcon color="var(--accent-base)" size={16} />}
              noExpandIcon
              alignItemsRight
            >
              <TopBarMenuItem
                key={'org-menu-item-1'}
                className={menuWithMobileStyles.mobileStaticMenuItem}
              >
                {currentOrganization
                  ? currentOrganization.title
                  : global.NOT_AVAILABLE}
              </TopBarMenuItem>
            </TopBarMenu>
          </div>
        )}

        {!isSimplyCheckView && (
          <div className={menuWithMobileStyles.menuContainer}>
            <TopBarMenu
              key={'user-menu'}
              title={
                user
                  ? `${user.firstName} ${user.lastName}`
                  : global.NOT_AVAILABLE
              }
              icon={
                <UserIcon variant="tiny" color="var(--accent-base)" size={16} />
              }
            >
              {getUserMenuItems(user, dispatch)}
            </TopBarMenu>
          </div>
        )}

        {showHideIcon && (
          <DownloadIcon
            onClick={() => {
              history.push('/my-downloads');
            }}
            animated={notifyStatus}
          />
        )}

        {saveFileId.length !== 0 && (
          <HighlightedNumber
            value={saveFileId.length}
            className={styles.fileCounter}
            onClick={() => {
              history.push('/my-downloads');
            }}
          />
        )}

        {!isSimplyCheckView && (
          <div className={menuWithMobileStyles.mobileMenuContainer}>
            <TopBarMenu
              key={'mobile-user-menu'}
              icon={
                <UserIcon variant="tiny" color="var(--accent-base)" size={16} />
              }
              noExpandIcon
              alignItemsRight
            >
              {getUserMenuItems(user, dispatch)}
            </TopBarMenu>
          </div>
        )}
      </div>
    </TopBar>
  );
};

const getUserMenuItems = (user, dispatch) => {
  return [
    <TopBarMenuItem
      key={'mobile-user-menu-item-1'}
      className={styles.mobileStaticMenuItem}
    >
      {user ? `${user.firstName} ${user.lastName}` : global.NOT_AVAILABLE}
    </TopBarMenuItem>,
    <TopBarMenuItem
      key={'mobile-user-menu-item-2'}
      className={menuWithMobileStyles.mobileStaticMenuItem}
    >
      {user ? `${user.firstName} ${user.lastName}` : global.NOT_AVAILABLE}
    </TopBarMenuItem>,
    <TopBarMenuItem
      className={menuWithMobileStyles.link}
      onSelect={() => {
        history.push('/my-downloads');
      }}
    >
      My Downloads
    </TopBarMenuItem>,
    <TopBarMenuItem
      key={'mobile-user-menu-item-3'}
      className={menuWithMobileStyles.link}
      onSelect={() => history.push('/settings/change-password')}
    >
      Change Password
    </TopBarMenuItem>,
    <TopBarMenuItem
      key={'mobile-user-menu-item-4'}
      className={styles.link}
      onSelect={() => dispatch(logout())}
    >
      Logout
    </TopBarMenuItem>,
  ];
};

export default TopBarContainer;
